import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Mail, Eye, Edit2, Trash2, Loader2, RefreshCw, BookmarkPlusIcon } from 'lucide-react';
import { PreviewModal } from '../components/PreviewModal';
import { EmailPreview } from '../components/EmailPreview';
import { emailTemplatesApi, EmailTemplateRecord, formConfigsApi, extractLookupId, emailTemplateLibraryApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { auditService } from '../services/auditService';
import { toast } from 'sonner';
export function EmailTemplates() {
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplateRecord | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [savingAsTemplate, setSavingAsTemplate] = useState<string | null>(null);
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await emailTemplatesApi.list();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to fetch email templates:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTemplates();
  }, []);
  const handleSaveAsTemplate = async (templateId: string) => {
    setSavingAsTemplate(templateId);
    try {
      const tpl = await emailTemplatesApi.get(templateId);
      if (!tpl) {
        toast.error('Could not load template data');
        return;
      }
      await emailTemplateLibraryApi.create({
        Template_Name: (tpl.Email_Subject || 'Untitled') + ' (Template)',
        Email_HTML_Body: tpl.Email_HTML_Body || '',
        Email_Subject: tpl.Email_Subject || '',
        Preheader_Text: tpl.Preheader_Text || '',
        Builder_Schema: (tpl as any).Builder_Schema || '',
        Builder_Mode: (tpl as any).Builder_Mode || 'code',
        Team: team,
        Region: tpl.Region || currentSubRegion || ''
      });
      toast.success('Saved as email template', {
        description: `"${tpl.Email_Subject}" saved to the template library.`,
        action: {
          label: 'View Library',
          onClick: () => navigate('/email-templates-library')
        },
        duration: 5000
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save as template', {
        description: msg
      });
    } finally {
      setSavingAsTemplate(null);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      try {
        const allConfigs = await formConfigsApi.list();
        const linkedConfigs = allConfigs.filter(c => {
          const templateId = extractLookupId(c.Email_Template);
          return templateId === id;
        });
        for (const config of linkedConfigs) {
          await formConfigsApi.delete(config.ID);
        }
        if (linkedConfigs.length > 0) {
          toast.info(`Removed ${linkedConfigs.length} linked configuration(s)`);
        }
      } catch (cleanupErr) {
        console.warn('Failed to clean up linked configurations:', cleanupErr);
      }
      await emailTemplatesApi.delete(id);
      setTemplates(prev => prev.filter(t => t.ID !== id));
      setDeleteConfirm(null);
      // Fire-and-forget: Audit log on delete
      const deletedTemplate = templates.find(t => t.ID === id);
      auditService.log({
        actionType: 'deleted',
        entityType: 'template',
        entityId: id,
        entityName: deletedTemplate?.Email_Subject || id,
        userName: currentUser.User_Name,
        userEmail: currentUser.Email,
        userRole: currentUser.Role,
        team: currentWorkspace.name,
        region: currentSubRegion || ''
      });
      toast.success('Email template deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete template', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  // Filter by team and region
  const filteredTemplates = templates.filter(t => {
    if (t.Team !== currentWorkspace.name && t.Team !== team) return false;
    if (currentSubRegion) {
      return !t.Region || t.Region === currentSubRegion;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-4055" data-mp="283jg02xiv30s">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4056" data-mp="2d0jpkntkynwn">
        <div className="min-w-0" data-id="element-4057" data-mp="3jgvfy2q5v1ho">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4058" data-mp="3jfu2jrloa2ws">
            Email Templates
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4059" data-mp="3odlu2kienn0g">
            Manage double opt-in and confirmation emails stored in Zoho Creator.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-4060" data-mp="3tk0popmq2yr7">
          <button onClick={fetchTemplates} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-4061" data-mp="3tmn0cy4wtudn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4062" data-mp="3ftov8k2o0nww" />
          </button>
          <Link to="/templates/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-4063" data-mp="3at1849hk4r7y">
            <Plus className="h-4 w-4" data-id="element-4064" data-mp="31uxltayqzqf3" />
            <span data-id="element-4065" data-mp="14333dm4921nz">Create Template</span>
          </Link>
        </div>
      </div>

      {loading && templates.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4066" data-mp="102sy6boa7fa6">
          <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4067" data-mp="2tjypvn0amric" />
          <p className="mt-2 text-sm text-gray-500" data-id="element-4068" data-mp="2yb6xqelz64te">
            Loading email templates...
          </p>
        </div> : filteredTemplates.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-4069" data-mp="2klmznm3c9vqf">
          <Mail className="mx-auto h-12 w-12 text-gray-400" data-id="element-4070" data-mp="2om6swekskf7a" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4071" data-mp="3cagd5c2cwuwb">
            No templates found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4072" data-mp="3hab4fgq1nmhw">
            Create your first email template to get started.
          </p>
          <Link to="/templates/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-4073" data-mp="339rwsaphqig9">
            <Plus className="h-4 w-4" data-id="element-4074" data-mp="1rtjv0bhvvb5n" />
            Create First Template
          </Link>
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6" data-id="element-4075" data-mp="1ws4u27lefosi">
          {filteredTemplates.map(template => <div key={template.ID} className="bg-white border border-gray-200 rounded-lg p-4 lg:p-6 flex flex-col" data-id="element-4076" data-mp="1kc54zs502wik">
              <div className="flex items-start justify-between mb-4 gap-3" data-id="element-4077" data-mp="1y9agw8reaplq">
                <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-4078" data-mp="1tctk6q64r2x1">
                  <Mail className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" data-id="element-4079" data-mp="15lxsru5l69qb" />
                </div>
                {template.Team && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-4080" data-mp="10pfund4rh9my">
                    {template.Team}
                  </span>}
              </div>
              <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-1 truncate" data-id="element-4081" data-mp="1ajzdejinuh3w">
                {template.Email_Subject || 'Untitled Template'}
              </h3>
              {template.Preheader_Text && <p className="text-sm text-gray-500 mb-4 truncate" data-id="element-4082" data-mp="3sjg01ajbxygw">
                  {template.Preheader_Text}
                </p>}
              <div className="flex items-center gap-2 pt-4 border-t border-gray-100 mt-auto flex-wrap" data-id="element-4083" data-mp="1dexxrhcef7e">
                <Link to={`/templates/${template.ID}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap" data-id="element-4084" data-mp="6aev1cw7s7md">
                  <Edit2 className="h-3 w-3" data-id="element-4085" data-mp="faldld6rrxse" /> Edit
                </Link>
                <span className="text-gray-300" data-id="element-4086" data-mp="ka6dbptrka66">|</span>
                <button onClick={() => setPreviewTemplate(template)} className="text-sm font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 whitespace-nowrap" data-id="element-4087" data-mp="p2r1obtwhv2b">
                  <Eye className="h-3 w-3" data-id="element-4088" data-mp="z1ygz4k73zs2" /> Preview
                </button>
                <span className="text-gray-300" data-id="element-4089" data-mp="131qpxidi47xy">|</span>
                <button onClick={() => handleSaveAsTemplate(template.ID)} disabled={savingAsTemplate === template.ID} className="text-sm font-medium text-amber-600 hover:text-amber-800 flex items-center gap-1 whitespace-nowrap disabled:opacity-50" data-id="element-4090" data-mp="13060u89yhh7h">
                  {savingAsTemplate === template.ID ? <Loader2 className="h-3 w-3 animate-spin" data-id="element-4091" data-mp="29m2zsjzj8rr0" /> : <BookmarkPlusIcon className="h-3 w-3" data-id="element-4092" data-mp="376z1dwa7tgdb" />}
                  Save as Template
                </button>
                <span className="text-gray-300" data-id="element-4093" data-mp="32bjph9vrpujj">|</span>
                <button onClick={() => setDeleteConfirm(template.ID)} className="text-sm font-medium text-red-500 hover:text-red-700 flex items-center gap-1 whitespace-nowrap" data-id="element-4094" data-mp="2xh613rfiwnod">
                  <Trash2 className="h-3 w-3" data-id="element-4095" data-mp="3q0ursvsrnwzs" /> Delete
                </button>
              </div>
            </div>)}
        </div>}

      {/* Preview Modal */}
      <PreviewModal isOpen={!!previewTemplate} onClose={() => setPreviewTemplate(null)} title={`Preview: ${previewTemplate?.Email_Subject}`} data-id="element-4096" data-mp="2v14h7ydzi14d">
        {previewTemplate && <EmailPreview subject={previewTemplate.Email_Subject} html={previewTemplate.Email_HTML_Body} data-id="element-4097" data-mp="350d6idxwmrke" />}
      </PreviewModal>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4098" data-mp="3dx43cska6ag1">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4099" data-mp="38zudjbszbwro">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4100" data-mp="3no8dnez0h74j" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4101" data-mp="3isjbr4zire52">
              <div className="flex items-center gap-4" data-id="element-4102" data-mp="ihhx9oqagaf">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4103" data-mp="3spe9hlnyxaia">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-4104" data-mp="f8kdy34yq8gc" />
                </div>
                <div data-id="element-4105" data-mp="k5avkuwf1c3v">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4106" data-mp="k5u5bobwwu6t">
                    Delete Email Template
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4107" data-mp="b5nt05rya098">
                    Are you sure? This will permanently remove the template from
                    Zoho Creator.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-4108" data-mp="18jgyyrtjl65">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4109" data-mp="3jlhb2p1xjjse">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-4110" data-mp="3emxm0fs1hrvt">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-4111" data-mp="1bexas7u61u5w" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}